import {Routes, Route, useLocation} from "react-router-dom"
import {Box, Grid, Typography, Tooltip, IconButton, Link} from "@material-ui/core"
import {Alert} from "@material-ui/lab"
import SnoozeIcon from "@material-ui/icons/Snooze"
import WarningIcon from "@material-ui/icons/Warning"
import React from "react"

import {Tabs} from "./Tabs"
import {CustomerMenu} from "./CustomerMenu"
import {Overview} from "./Overview"
import {Accounts} from "./Accounts"
import {PaymentPlans} from "./PaymentPlans/PaymentPlans"
import {Notes} from "./Notes"
import {Transactions} from "./Transactions"
import {Activities} from "./Activities"
import {CustomerOffers} from "./CustomerOffers"
import {OfferModal} from "./OfferModal"
import IconDiscount from "./IconDiscount.svg"
import {CustomerEligibleOffersModal} from "./CustomerEligibleOffersModal"

import {SystemOfRecordChip} from "@indebted/components/SystemOfRecordChip"
import {useNotification} from "@indebted/components/Notification"
import {api, Customer, SettlementOfferResponse} from "@indebted/api"
import {Search} from "@indebted/components/Search"

function CustomerCard({customer, reload}: {customer: Customer; reload: () => void}) {
	const location = useLocation()
	const search = new URLSearchParams(location.search)
	const [offerData, setOfferData] = React.useState<
		| ({
				open: boolean
		  } & SettlementOfferResponse)
		| undefined
	>()
	const {notification} = useNotification()

	function setupDiscountOffer() {
		api.customer
			.getSettlementOffer(customer.ID)
			.then((response) => {
				setOfferData({...response, open: true})
			})
			.catch((error: {Message: string}) => {
				notification.error(error.Message)
			})
	}

	function OverviewTab() {
		return (
			<>
				<Tabs index={0} customer={customer} />
				<Overview customer={customer} reload={reload} />
			</>
		)
	}
	function NoteTab() {
		return (
			<>
				<Tabs index={1} customer={customer} />
				<Notes
					customerID={customer.ID}
					notes={customer.Notes}
					reload={reload}
					systemOfRecord={customer.SystemOfRecord}
				/>
			</>
		)
	}

	function AccountsTab() {
		return (
			<>
				<Tabs index={2} customer={customer} />
				<Accounts customer={customer} reload={reload} />
			</>
		)
	}

	function TransactionsTab() {
		return (
			<>
				<Tabs index={3} customer={customer} />
				<Transactions transactions={customer.Transactions} />
			</>
		)
	}

	function PaymentPlansTab() {
		return (
			<>
				<Tabs index={4} customer={customer} />
				<PaymentPlans
					activePaymentPlans={customer.ActivePaymentPlans}
					reload={reload}
					customerID={customer.ID}
					totalCustomerBalance={customer.TotalCustomerBalanceInCents / 100}
				/>
			</>
		)
	}

	function ActivitiesTab() {
		return (
			<>
				<Tabs index={5} customer={customer} />
				<Activities customerID={customer.ID} />
			</>
		)
	}

	function OffersTab() {
		return (
			<>
				<Tabs index={6} customer={customer} />
				<CustomerOffers
					customerID={customer.ID}
					reload={reload}
					enableOneOffOffers={customer.EnableOneOffOffers}
					enablePlanOffers={customer.EnablePlanOffers}
					minInstalmentAmount={10}
				/>
			</>
		)
	}

	function AccountClosureAlert({customer}: {customer: Customer}) {
		const allAccounts = [
			...customer.OtherAccounts,
			...customer.OutstandingAccounts,
			...customer.PaymentPlanAccounts,
		]
		const closedAccounts = allAccounts.filter((acc) => acc.Closed)
		const hasClosedAccounts = closedAccounts.length > 0
		const hasAllAccountsClosed = hasClosedAccounts && allAccounts.length == closedAccounts.length

		return (
			<>
				{hasClosedAccounts && (
					<Grid item style={{flexGrow: 1, paddingTop: "5px"}}>
						{hasAllAccountsClosed ? (
							<Alert severity="error">Account(s) closed for collection</Alert>
						) : (
							<Alert severity="warning">Customer has both open and closed accounts for collection</Alert>
						)}
					</Grid>
				)}
			</>
		)
	}

	return (
		<>
			{offerData ? <OfferModal offerData={offerData} customerID={customer.ID} /> : null}
			<Search value={search.get("q")} backURL={search.get("back")} />
			<Box p={3} pt={0}>
				{customer.SystemOfRecord === "Latitude" && (
					<Grid container style={{marginBottom: "16px"}}>
						<Grid item xs={12}>
							<Alert severity="warning">
								This is a Latitude customer. No action should be taken in Rocket.
								{customer.Latitude && (
									<>
										{" "}
										{customer.Latitude.Link && (
											<Link
												href={customer.Latitude.Link}
												target="_blank"
												rel="noopener noreferrer"
											>
												View in Latitude
											</Link>
										)}
										<br />
										Collection Comms Enabled:{" "}
										{customer.Latitude.CollectionCommsEnabled ? "Yes" : "No"}
										<br />
										Payments Enabled: {customer.Latitude.PaymentsEnabled ? "Yes" : "No"}
									</>
								)}
							</Alert>
						</Grid>
					</Grid>
				)}
				<Grid container alignItems="center">
					<Grid item style={{display: "flex", alignItems: "center"}}>
						<CustomerStatus customer={customer} />
					</Grid>
					<Grid item style={{flexGrow: 1}}>
						<Box display="flex" alignItems="center">
							<Typography variant="h5">{customer.FullName}</Typography>
							<SystemOfRecordChip systemOfRecord={customer.SystemOfRecord} />
						</Box>
					</Grid>
					{!customer.CustomerPaidCommissions && (
						<>
							<Grid item>
								<CustomerEligibleOffersModal customerID={customer.ID} />
							</Grid>
							{customer.EnableCreateSettlementOffer && (
								<Grid item>
									<IconButton onClick={setupDiscountOffer} title="Settle account with discount">
										<img src={IconDiscount} width={20} />
									</IconButton>
								</Grid>
							)}
						</>
					)}
					<Grid item>
						<CustomerMenu customer={customer} reload={reload} />
					</Grid>
				</Grid>
				<AccountClosureAlert customer={customer} />
			</Box>

			<Routes>
				<Route path="/" element={<OverviewTab />} />
				<Route path="/notes" element={<NoteTab />} />
				<Route path="/accounts/*" element={<AccountsTab />} />
				<Route path="/paymentplans" element={<PaymentPlansTab />} />
				<Route path="/activities/*" element={<ActivitiesTab />} />
				<Route path="/transactions" element={<TransactionsTab />} />
				{customer.EnableRocketOffersTab && <Route path="/offers" element={<OffersTab />} />}
			</Routes>
		</>
	)
}

function CustomerStatus({customer}: {customer: Customer}): JSX.Element | null {
	const snoozeMap: Record<string, JSX.Element | null> = {
		true: (
			<Tooltip title={`until ${customer.SnoozedUntil}`} placement="right">
				<SnoozeIcon style={{paddingRight: "0.5rem", fontSize: "2rem"}} />
			</Tooltip>
		),
		false: null,
	}

	const escalationMap: Record<string, JSX.Element | null> = {
		true: (
			<Tooltip title={customer.Escalation} placement="right">
				<WarningIcon style={{paddingRight: "0.5rem", fontSize: "2rem"}} />
			</Tooltip>
		),
		false: null,
	}

	return escalationMap[String(!!customer.Escalation)] || snoozeMap[String(!!customer.CommunicationSnoozed)]
}

export {CustomerCard}
